// Read-only agent-version detail Drawer view (docs/09 §4/§8). The
// immutable version is addressed by `?version=<uuid>`; the Drawer's
// accessible name supplies its only heading (UI-LAW §4a).

import type { components } from "../../api/schema";
import { formatDateTime } from "../../lib/format";
import {
  Button,
  Copyable,
  Drawer,
  Inline,
  InlineError,
  KeyValue,
  Skeleton,
  Stack,
} from "../../ui";

type AgentVersion = components["schemas"]["AgentVersionResponse"];

export interface AgentVersionDetailDrawerViewProps {
  errorMessage?: string;
  isOpen: boolean;
  isPending: boolean;
  onDismiss: () => void;
  version?: AgentVersion;
}

export function AgentVersionDetailDrawerView({
  errorMessage,
  isOpen,
  isPending,
  onDismiss,
  version,
}: AgentVersionDetailDrawerViewProps) {
  const resolvedError =
    !isPending && isOpen && version === undefined && errorMessage === undefined
      ? "Agent version not found."
      : errorMessage;

  return (
    <Drawer
      ariaLabel="Agent version detail"
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="md"
    >
      {isPending ? (
        <Skeleton label="Loading agent version" rows={5} shape="detail-field" />
      ) : resolvedError !== undefined ? (
        <Stack gap={4}>
          <InlineError>{resolvedError}</InlineError>
          <Inline>
            <Button type="button" variant="secondary" onClick={onDismiss}>
              Close
            </Button>
          </Inline>
        </Stack>
      ) : version !== undefined ? (
        <KeyValue
          rows={[
            { label: "Tag", value: version.tag },
            { label: "Created", value: formatDateTime(version.created_at) },
            {
              label: "Created by",
              value: version.created_by ?? "System",
            },
            {
              label: "Version ID",
              value: <Copyable label="Version ID" value={version.id} />,
            },
            {
              label: "Agent ID",
              value: <Copyable label="Agent ID" value={version.agent_id} />,
            },
          ]}
        />
      ) : null}
    </Drawer>
  );
}
